/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { PAYMENT_TYPE } from "constants/paymentType";
import { format } from "date-fns";
import { useI18n } from "hooks/i18n/i18n";
import { PAYMENT_STATUS } from "pages/AccountSettlementHistoryOrderId/constants/PaymentStatus";
import {
  hideOnDesktopView,
  infoWrapperStyle,
  infoTitleTextStyle,
  infoDetailTextStyle,
} from "pages/AccountSettlementHistoryOrderId/styles/commonStyles";
import { getDeliveryCarrierName } from "pages/AccountSettlementHistoryOrderId/utils/getDeliveryCarrierName";
import { getPaymentStatusLabel } from "pages/AccountSettlementHistoryOrderId/utils/getPaymentStatusLabel";
import { Fragment } from "react";
import Skeleton from "react-loading-skeleton";
import { StatusLabel } from "ui/StatusLabel";
import { SHIPMENT_STATUS } from "ui/StatusLabel/constants/shipmentStatus";
import { isPayPalPendingError } from "utility/paypal";

import { Copy } from "../icons/Copy";
import { ReceiptOrPaymentAlert } from "./components/ReceiptOrPaymentAlert";
import { TrackingButton } from "./components/TrackingButton";
import {
  alertTextStyle,
  copyButtonWrapperStyle,
  orderInfoWrapperStyle,
  orderNumberRowStyle,
  purchaseDateStyle,
  rootStyle,
  topContentStyle,
  trackingInfoWrapperStyle,
} from "./styles";

import type { ActiveTransaction } from "@spwn/types/firebase/firestore";
import type { DeliveryData } from "pages/AccountSettlementHistoryOrderId/types/DeliveryData";
import type { FulfillmentData } from "pages/AccountSettlementHistoryOrderId/types/FulfillmentData";
import type { OrderData } from "pages/AccountSettlementHistoryOrderId/types/OrderData";
import type { FC } from "react";

type Props = {
  deliveryData?: DeliveryData;
  fulfillmentData?: FulfillmentData;
  isLoadingAddress: boolean;
  isLoadingFulfillment: boolean;
  isPurchased: boolean;
  onContinuePayment: () => void;
  onCopyOrderId: (orderId: number) => void;
  onShowReceipt: () => void;
  orderData: OrderData;
  transaction?: ActiveTransaction;
};

const formatPurchaseDate = (purchasedAt: number, language: string): string => {
  const date = new Date(purchasedAt * 1000);
  if (isNaN(date.getTime())) return "";
  const formatPattern = language === "ja" ? "yyyy年MM月dd日" : "yyyy/MM/dd";
  return format(date, formatPattern);
};

export const OrderInfo: FC<Props> = (props) => {
  const {
    deliveryData,
    fulfillmentData,
    isLoadingAddress,
    isLoadingFulfillment,
    isPurchased,
    onContinuePayment,
    onCopyOrderId,
    onShowReceipt,
    orderData,
    transaction,
  } = props;
  const {
    orderId,
    purchasedAt,
    purchasedCount,
    purchasedType,
    paymentStatus,
    totalPrice,
  } = orderData;

  const { recipientName, postalCode, mainAddress, subAddress } =
    deliveryData ?? {};

  const formattedPrice = totalPrice.toLocaleString("ja-JP");

  const { t, i18n } = useI18n();

  const formattedDate = formatPurchaseDate(purchasedAt, i18n.language);

  const shouldShowShippingInfo =
    !isLoadingFulfillment && fulfillmentData?.shippingStatus === "shipped";

  const isPayPalPending =
    transaction?.payType === PAYMENT_TYPE.PayPal &&
    transaction.status === PAYMENT_STATUS.UNPROCESS &&
    isPayPalPendingError(transaction.errorInfo ?? null);

  const showPaymentAlert =
    !isPurchased && transaction?.status === PAYMENT_STATUS.UNPROCESS;

  return (
    <div css={rootStyle}>
      <div css={topContentStyle}>
        <span css={purchaseDateStyle}>{formattedDate}</span>
        {isLoadingFulfillment && <Skeleton width={72} height={24} />}
        {shouldShowShippingInfo && (
          <StatusLabel status={SHIPMENT_STATUS.SHIPPED} />
        )}
      </div>
      <div css={orderInfoWrapperStyle}>
        <h4 css={infoTitleTextStyle}>{t("settlementHistory.orderNumber")}</h4>
        <div css={orderNumberRowStyle}>
          <p css={infoDetailTextStyle}>
            <span>{orderId}</span>
          </p>
          <button
            css={copyButtonWrapperStyle}
            onClick={() => onCopyOrderId(orderId)}
            aria-label={t("settlementHistory.copyOrderId")}
          >
            <Copy />
          </button>
        </div>
      </div>
      {isLoadingFulfillment ? (
        <div css={trackingInfoWrapperStyle}>
          <p css={infoDetailTextStyle}>
            <Skeleton width={150} />
          </p>
          <p css={infoDetailTextStyle}>
            <Skeleton width={200} />
          </p>
          <Skeleton width={140} height={30} />
        </div>
      ) : (
        shouldShowShippingInfo &&
        fulfillmentData.trackingCode && (
          <div css={trackingInfoWrapperStyle}>
            {fulfillmentData.carrier && (
              <p css={infoDetailTextStyle}>
                {t("settlementHistory.delivery.carrier.label")}:{" "}
                {t(getDeliveryCarrierName(fulfillmentData.carrier))}
              </p>
            )}
            <p css={infoDetailTextStyle}>
              {t("settlementHistory.trackingNumber")}:{" "}
              {fulfillmentData.trackingCode}
            </p>
            <TrackingButton
              trackingCode={fulfillmentData.trackingCode}
              url={fulfillmentData.url}
            />
          </div>
        )
      )}
      {isLoadingAddress ? (
        <div css={infoWrapperStyle}>
          <h4 css={infoTitleTextStyle}>
            {t("settlementHistory.delivery.destination")}
          </h4>
          <p css={infoDetailTextStyle}>
            <Skeleton width={100} />
            <br />
            <Skeleton width={80} />
            <br />
            <Skeleton width={200} />
          </p>
        </div>
      ) : (
        shouldShowShippingInfo &&
        deliveryData && (
          <div css={infoWrapperStyle}>
            <h4 css={infoTitleTextStyle}>
              {t("settlementHistory.delivery.destination")}
            </h4>
            <p css={infoDetailTextStyle}>
              {recipientName}
              <br />〒{postalCode}
              <br />
              {mainAddress}
              {subAddress && ` ${subAddress}`}
            </p>
          </div>
        )
      )}
      {/* 一つの段落になるため、Fragmentで分離 */}
      <Fragment>
        <h4 css={[infoTitleTextStyle, hideOnDesktopView]}>
          {t("settlementHistory.orderContent")}
        </h4>
        <p css={infoDetailTextStyle}>
          {t("settlementHistory.totalCount")}: {purchasedCount}
          {t("settlementHistory.countUnit")}
        </p>
        <p css={infoDetailTextStyle}>
          {t("settlementHistory.paymentAmount")}: ¥{formattedPrice}{" "}
        </p>
      </Fragment>
      <div css={infoWrapperStyle}>
        <p css={infoTitleTextStyle}>
          {t("settlementHistory.paymentMethodLabel")}
        </p>
        <p>
          <span css={infoDetailTextStyle}>{t(purchasedType)}: </span>
          <span
            css={
              paymentStatus === PAYMENT_STATUS.PAYSUCCESS
                ? infoDetailTextStyle
                : alertTextStyle
            }
          >
            {t(getPaymentStatusLabel(paymentStatus))}
          </span>
        </p>
        <ReceiptOrPaymentAlert
          isPayPalPending={isPayPalPending}
          isPurchased={isPurchased}
          onContinuePayment={onContinuePayment}
          onShowReceipt={onShowReceipt}
          showPaymentAlert={showPaymentAlert}
        />
      </div>
    </div>
  );
};
